// src/components/QuoteGrid.js

import React, { useMemo } from 'react';
// --- NOVÉ MODULÁRNÍ IMPORTY ---
import { AgGridReact } from '@ag-grid-community/react';
import { ModuleRegistry } from '@ag-grid-community/core';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';

// --- REGISTRACE POTŘEBNÝCH MODULŮ ---
ModuleRegistry.registerModules([
    ClientSideRowModelModule,
]);

// Formátovače
const numberFormatter = (params) => { if (params.value == null || isNaN(params.value)) return ''; return new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value); };
const currencyFormatter = (params) => { if (params.value == null || isNaN(params.value)) return ''; return new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(params.value); };
const percentageFormatterForCell = (params) => { 
    if (params.value == null || isNaN(params.value)) return ''; 
    return new Intl.NumberFormat('cs-CZ', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
    }).format(params.value) + ' %'; 
};
const percentageFormatterForTooltip = (params) => {
    if (params.value == null || isNaN(params.value)) return '';
    const preciseValue = new Intl.NumberFormat('cs-CZ', {
        minimumFractionDigits: 6,
        maximumFractionDigits: 6
    }).format(params.value);
    return `Exact value: ${preciseValue} %`;
};


// Definice sloupců
const createColumnDefs = (props) => {
    const { onShowItemDetails, onOpenTargetPriceModal, userRole } = props;

    const isSales = userRole === 'KD_SALES';
    const isPurchase = userRole === 'KD_PURCHASE' || isSales;
    const isEngineering = userRole === 'KD_ENGINEERING' || isPurchase || isSales;
    
    // Definice pro sloupec s ikonou pro editaci
    const editColDef = {
        headerName: '',
        width: 80,
        resizable: false, sortable: false, filter: false, floatingFilter: false,
        pinned: 'left',
        checkboxSelection: true,
        headerCheckboxSelection: true,
        cellRenderer: (params) => (
            <div className="grid-actions-cell">
                <button
                    className="grid-action-btn"
                    title="Edit Item Details"
                    onClick={() => onShowItemDetails(params.data)}
                >
                    ✏️
                </button>
            </div>
        )
    };
    
    // Definice pro sloupec s ikonou pro cílovou cenu
    const targetPriceColDef = {
        headerName: '',
        width: 60,
        resizable: false, sortable: false, filter: false, floatingFilter: false,
        cellRenderer: (params) => (
            <div className="grid-actions-cell">
                <button
                    className="grid-action-btn"
                    title="Target Price Calculator"
                    onClick={() => onOpenTargetPriceModal(params.data)}
                >
                    🎯
                </button>
            </div>
        )
    };

    const discountColumnProps = {
        editable: isSales,
        type: 'numericColumn',
        valueFormatter: percentageFormatterForCell,
        tooltipValueGetter: percentageFormatterForTooltip,
    };

    const engineeringColumnDefs = [
        editColDef,
        { field: 'Item_Sequence', headerName: '#', width: 80, type: 'numericColumn', pinned: 'left', rowDrag: true, filter: false, comparator: (valueA, valueB) => valueA - valueB, },
        { field: 'Product_Name', headerName: 'Product', flex: 1, minWidth: 180, pinned: 'left', editable: isEngineering },
        { field: 'Options_Code', headerName: 'Options', width: 120, editable: isEngineering },
        { field: 'Description', headerName: 'Description', flex: 2, editable: isEngineering, cellEditor: 'agLargeTextCellEditor', cellEditorPopup: true, cellEditorParams: { maxLength: 1000, rows: 10, cols: 60 }, tooltipField: 'Description' },
        { field: 'Item_Notes', headerName: 'Item Notes', width: 200, editable: isEngineering, cellEditor: 'agLargeTextCellEditor', cellEditorPopup: true },
        { field: 'Quantity', headerName: 'QTY', width: 75, editable: isEngineering, type: 'numericColumn', valueFormatter: numberFormatter },
        { headerName: 'TECHNICAL DATA', children: [ { field: 'Weight', headerName: 'Weight (kg)', width: 100, editable: isEngineering, type: 'numericColumn', valueFormatter: numberFormatter }, { field: 'Delivery_Time', headerName: 'Lead Time (wd)', width: 90, editable: isEngineering, type: 'numericColumn' }, { field: 'Origin', headerName: 'Origin', width: 80, editable: isEngineering }, { field: 'HS_Code', headerName: 'HS Code', width: 120, editable: isEngineering }, ]},
    ];

    const purchaseColumnDefs = [
        editColDef,
        { field: 'Item_Sequence', headerName: '#', width: 80, type: 'numericColumn', pinned: 'left', rowDrag: true, filter: false, comparator: (valueA, valueB) => valueA - valueB, },
        { field: 'Product_Name', headerName: 'Product', flex: 1, minWidth: 180, pinned: 'left', editable: false },
        { field: 'Quantity', headerName: 'QTY', width: 75, editable: isPurchase, type: 'numericColumn', valueFormatter: numberFormatter },
        { headerName: 'PURCHASE', children: [ 
            { field: 'List_Price', headerName: 'List Price', width: 120, editable: isPurchase, type: 'numericColumn', valueFormatter: currencyFormatter }, 
            { field: 'OP_Discount', headerName: 'OP Disc. %', width: 90, editable: isPurchase, type: 'numericColumn', valueFormatter: percentageFormatterForCell, tooltipValueGetter: percentageFormatterForTooltip }, 
            { headerName: 'OP Unit', field: 'OP_Price', width: 120, type: 'numericColumn', valueFormatter: currencyFormatter, valueGetter: params => params.data.List_Price * (1 - (params.data.OP_Discount || 0) / 100) }, 
            { headerName: 'Goods Total', field: 'Goods_Total', width: 120, type: 'numericColumn', valueFormatter: currencyFormatter, valueGetter: params => (params.getValue('OP_Price') || 0) * (params.data.Quantity || 0) }, 
            { field: 'AsCost_Unit', headerName: 'AsCost Unit', width: 120, type: 'numericColumn', valueFormatter: currencyFormatter }, 
            { field: 'OP_AsCosts', headerName: 'AsCost Total', width: 120, type: 'numericColumn', valueFormatter: currencyFormatter }, 
            { headerName: 'OP Total', field: 'Final_OP_Total', width: 120, type: 'numericColumn', valueFormatter: currencyFormatter, cellStyle: { fontWeight: 'bold' }, valueGetter: params => (params.getValue('Goods_Total') || 0) + (params.data.OP_AsCosts || 0) } 
        ]},
        { field: 'Source', headerName: 'Source', width: 120, editable: isPurchase },
        { field: 'Purchase_Notes', headerName: 'Purchase Notes', width: 200, editable: isPurchase, cellEditor: 'agLargeTextCellEditor', cellEditorPopup: true }
    ];

    const quotationColumnDefs = [
        editColDef,
        { field: 'Item_Sequence', headerName: '#', width: 80, type: 'numericColumn', pinned: 'left', rowDrag: true, filter: false, comparator: (valueA, valueB) => valueA - valueB, },
        { field: 'Product_Name', headerName: 'Product', flex: 1, minWidth: 180, pinned: 'left' },
        { field: 'Quantity', headerName: 'QTY', width: 75, editable: isSales, type: 'numericColumn', valueFormatter: numberFormatter },
        { field: 'List_Price', headerName: 'List Price', width: 120, editable: isSales, type: 'numericColumn', valueFormatter: currencyFormatter },
        { headerName: 'PURCHASE', children: [ { headerName: 'OP Unit', width: 120, type: 'numericColumn', valueFormatter: currencyFormatter, valueGetter: params => { const listPrice = params.data.List_Price || 0; const opDisc = params.data.OP_Discount || 0; const asCostUnit = params.data.AsCost_Unit || 0; const opPrice = listPrice * (1 - opDisc / 100); return opPrice + asCostUnit; } }, { headerName: 'OP Total', field: 'Final_OP_Total', width: 120, type: 'numericColumn', valueFormatter: currencyFormatter, valueGetter: params => { const listPrice = params.data.List_Price || 0; const opDisc = params.data.OP_Discount || 0; const asCosts = params.data.OP_AsCosts || 0; const quantity = params.data.Quantity || 1; const goodsTotal = (listPrice * (1 - opDisc / 100)) * quantity; return goodsTotal + asCosts; } } ]},
        { headerName: 'SALE', children: [ 
            targetPriceColDef,
            { field: 'RP_Discount', headerName: 'RP Disc.', width: 90, ...discountColumnProps }, 
            { headerName: 'RP Unit', field: 'RP_Price', width: 120, type: 'numericColumn', valueFormatter: currencyFormatter, valueGetter: params => (params.data.List_Price || 0) * (1 - (params.data.RP_Discount || 0) / 100) },
            { field: 'CP_Discount', headerName: 'CP Disc.', width: 90, ...discountColumnProps }, 
            { headerName: 'CP unit', field: 'CP_Price', width: 120, type: 'numericColumn', valueFormatter: currencyFormatter, valueGetter: params => (params.getValue('RP_Price') || 0) * (1 - (params.data.CP_Discount || 0) / 100) }, 
            { headerName: 'CP Total', width: 120, type: 'numericColumn', valueFormatter: currencyFormatter, valueGetter: params => (params.getValue('CP_Price') || 0) * (params.data.Quantity || 0) } 
        ]},
{ 
            headerName: 'MARGIN', 
            children: [ 
                { 
                    headerName: 'Margin', 
                    field: 'Margin_Abs', 
                    width: 120, 
                    type: 'numericColumn', 
                    valueFormatter: currencyFormatter, 
                    valueGetter: params => { 
                        const cpTotal = (params.getValue('CP_Price') || 0) * (params.data.Quantity || 0); 
                        const finalOpTotal = params.getValue('Final_OP_Total') || 0; 
                        return cpTotal - finalOpTotal; 
                    },
                    // --- ZMĚNA ZDE ---
                    cellClassRules: {
                        'negative-margin': params => params.value < 0
                    }
                }, 
                { 
                    headerName: 'Margin', 
                    field: 'Margin_Rel', 
                    width: 100, 
                    type: 'numericColumn', 
                    valueFormatter: params => params.value != null ? `${params.value.toFixed(2)} %` : '', 
                    valueGetter: params => { 
                        const finalOpTotal = params.getValue('Final_OP_Total') || 0; 
                        if (finalOpTotal === 0) return 0; 
                        const marginAbs = params.getValue('Margin_Abs'); 
                        return (marginAbs / finalOpTotal) * 100; 
                    },
                    // --- ZMĚNA ZDE ---
                    cellClassRules: {
                        'negative-margin': params => params.value < 0
                    }
                } 
            ]
        }    ];
    
    return { engineeringColumnDefs, purchaseColumnDefs, quotationColumnDefs };
};


const QuoteGrid = (props) => {
    const { 
        gridRef, 
        rowData, 
        currentStep, 
        onCellValueChanged, 
        onRowDragEnd
    } = props;

    const columnDefs = useMemo(() => createColumnDefs(props), [props]);

    const getCurrentColumnDefs = () => {
        switch (currentStep) {
            case 1: return columnDefs.engineeringColumnDefs;
            case 2: return columnDefs.purchaseColumnDefs;
            case 3: return columnDefs.quotationColumnDefs;
            default: return [];
        }
    };

    // Note: Do not override built-in 'numericColumn' type (AG Grid warns). Use default behavior instead.

    return (
        <div className="ag-grid-container ag-theme-alpine">
            <AgGridReact
                ref={gridRef}
                rowData={rowData}
                columnDefs={getCurrentColumnDefs()}
                defaultColDef={{ 
                    sortable: true, 
                    resizable: true, 
                    filter: true, 
                    floatingFilter: true, 
                    wrapText: true, 
                    autoHeight: true,
                    enableCellChangeFlash: true
                }}
                tooltipShowDelay={500}
                rowSelection={'multiple'}
                onCellValueChanged={onCellValueChanged}
                getRowId={params => (params.data && params.data.ROWID) ? params.data.ROWID.toString() : String(Math.random())}
                animateRows={true}
                rowDragManaged={true}
                onRowDragEnd={onRowDragEnd}
                suppressSortOnDragEnd={true}
            />
        </div>
    );
};

export default QuoteGrid;